import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Link2, Search, FileText, CheckCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePurchaseInvoices } from '@/hooks/usePurchaseInvoices';
import { formatCurrency } from '@/utils/formatters';
import { toast } from 'sonner';
import type { PendingPurchaseInvoice } from '@/types/purchaseInvoice';

interface LinkInvoiceDialogProps {
  invoice: PendingPurchaseInvoice;
  open: boolean;
  onClose: () => void;
}

export function LinkInvoiceDialog({ invoice, open, onClose }: LinkInvoiceDialogProps) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { updateInvoiceStatus, manifestInvoice } = usePurchaseInvoices();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPurchaseId, setSelectedPurchaseId] = useState<string | null>(null);
  const [isLinking, setIsLinking] = useState(false);

  // Get company ID
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('user_id', user.id)
        .single();
      return data;
    },
    enabled: !!user?.id,
  });

  const companyId = profile?.company_id;

  // Fetch purchases without invoice key that match criteria
  const { data: purchases = [], isLoading } = useQuery({
    queryKey: ['purchases-for-linking', companyId, invoice.emitente_cnpj, invoice.total_value, searchTerm],
    queryFn: async () => {
      if (!companyId) return [];

      let query = supabase
        .from('purchases')
        .select(`
          *,
          supplier:supplier_id (
            id, name, document_number
          )
        `)
        .eq('company_id', companyId)
        .is('invoice_key', null)
        // A-sept.1: compra excluída (soft-delete) não é candidata a vínculo
        .is('deleted_at', null)
        .order('purchase_date', { ascending: false })
        .limit(20);

      // Filter by search term
      if (searchTerm) {
        query = query.ilike('purchase_number', `%${searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Sort by relevance (same supplier/value first)
      const sortedData = (data || []).sort((a, b) => {
        const aMatchesSupplier = a.supplier?.document_number === invoice.emitente_cnpj?.replace(/\D/g, '');
        const bMatchesSupplier = b.supplier?.document_number === invoice.emitente_cnpj?.replace(/\D/g, '');
        const aMatchesValue = Math.abs((a.total_value || 0) - (invoice.total_value || 0)) < 1;
        const bMatchesValue = Math.abs((b.total_value || 0) - (invoice.total_value || 0)) < 1;

        if (aMatchesSupplier && aMatchesValue && !(bMatchesSupplier && bMatchesValue)) return -1;
        if (bMatchesSupplier && bMatchesValue && !(aMatchesSupplier && aMatchesValue)) return 1;
        if (aMatchesSupplier && !bMatchesSupplier) return -1;
        if (bMatchesSupplier && !aMatchesSupplier) return 1;
        if (aMatchesValue && !bMatchesValue) return -1;
        if (bMatchesValue && !aMatchesValue) return 1;
        return 0;
      });

      return sortedData;
    },
    enabled: !!companyId,
  });

  const handleLink = async () => {
    if (!selectedPurchaseId) return;
    // A-sept.1: sem tenant resolvido não há UPDATE (o filtro por company_id é obrigatório)
    if (!companyId) return;

    setIsLinking(true);
    try {
      // Update purchase with invoice data
      // A-sept.1: UPDATE direto em purchases (fora do motor) — cercado por tenant, compra viva e
      // chave AINDA vazia (R-COMPRAS.19: a chave arma a trava fiscal; nunca sobrescrever uma já
      // gravada por corrida entre abas). .select('id') + 0 linhas = erro: sem isso o filtro viraria
      // "sucesso" silencioso e a nota seria marcada 'linked' sem vínculo.
      const { data: linkedRows, error: purchaseError } = await supabase
        .from('purchases')
        .update({
          invoice_key: invoice.access_key,
          invoice_number: invoice.invoice_number,
          invoice_series: invoice.invoice_series,
          updated_at: new Date().toISOString(),
        })
        .eq('id', selectedPurchaseId)
        .eq('company_id', companyId)
        .is('deleted_at', null)
        .is('invoice_key', null)
        .select('id');

      if (purchaseError) throw purchaseError;
      if (!linkedRows || linkedRows.length === 0) {
        throw new Error('Compra não encontrada, excluída, de outra empresa ou já vinculada a outra nota');
      }

      // Update pending invoice status
      await updateInvoiceStatus({
        invoiceId: invoice.id,
        status: 'linked',
        linkedPurchaseId: selectedPurchaseId,
      });

      // Manifest confirmation
      await manifestInvoice({
        invoiceId: invoice.id,
        manifestationType: 'confirmacao',
      });

      toast.success('Nota fiscal vinculada com sucesso!');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchases-for-linking'] });
      onClose();
    } catch (error) {
      console.error('Error linking invoice:', error);
      // A-sept.1: o motivo chega ao operador (o throw de 0 linhas não pode morrer num toast genérico)
      toast.error('Erro ao vincular nota fiscal', {
        description: (error as { message?: string } | null)?.message,
      });
    } finally {
      setIsLinking(false);
    }
  };

  const isMatch = (purchase: any) => {
    const matchesSupplier = purchase.supplier?.document_number === invoice.emitente_cnpj?.replace(/\D/g, '');
    const matchesValue = Math.abs((purchase.total_value || 0) - (invoice.total_value || 0)) < 1;
    return matchesSupplier && matchesValue;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!isLinking) onClose(); }}>
      <DialogContent 
        className="max-w-2xl"
        onEscapeKeyDown={(e) => { if (isLinking) e.preventDefault(); }}
        onInteractOutside={(e) => { if (isLinking) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            Vincular NF-e a uma Compra Existente
          </DialogTitle>
        </DialogHeader>

        {/* Invoice Summary */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="h-4 w-4 text-primary" />
            <span className="font-medium">
              NF-e {invoice.invoice_series || '1'}-{invoice.invoice_number}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <span className="text-muted-foreground">Fornecedor:</span>{' '}
              <span className="font-medium">{invoice.emitente_razao_social}</span>
            </div>
            <div>
              <span className="text-muted-foreground">Valor:</span>{' '}
              <span className="font-medium">{formatCurrency(invoice.total_value || 0)}</span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por número da compra..."
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Purchases List */}
        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : purchases.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-center">
              <FileText className="h-8 w-8 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Nenhuma compra sem nota fiscal encontrada
              </p>
            </div>
          ) : (
            <RadioGroup
              value={selectedPurchaseId || ''}
              onValueChange={setSelectedPurchaseId}
              className="space-y-2"
            >
              {purchases.map((purchase) => {
                const match = isMatch(purchase);
                
                return (
                  <div
                    key={purchase.id}
                    className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedPurchaseId === purchase.id
                        ? 'border-primary bg-primary/5'
                        : 'hover:bg-muted/50'
                    } ${match ? 'border-green-200 bg-green-50/50' : ''}`}
                    onClick={() => setSelectedPurchaseId(purchase.id)}
                  >
                    <RadioGroupItem value={purchase.id} id={purchase.id} />
                    <Label htmlFor={purchase.id} className="flex-1 cursor-pointer">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">
                              Compra #{purchase.purchase_number}
                            </span>
                            {match && (
                              <Badge variant="outline" className="bg-green-100 text-green-700 border-green-200">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Compatível
                              </Badge>
                            )}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {purchase.supplier?.name || 'Fornecedor não identificado'}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-medium">
                            {formatCurrency(purchase.total_value || 0)}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {purchase.purchase_date 
                              ? format(new Date(purchase.purchase_date), 'dd/MM/yyyy', { locale: ptBR })
                              : '-'}
                          </div>
                        </div>
                      </div>
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          )}
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={handleLink}
            disabled={!selectedPurchaseId || isLinking}
          >
            {isLinking ? 'Vinculando...' : 'Vincular'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
